import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Typography,
  TextField,
  InputAdornment,
  Box,
  IconButton
} from '@mui/material';
import { Search, Close } from '@mui/icons-material';
import baseurl from '../Baseurl/baseurl';
import { Button, Avatar, Eyebrow, EmptyState } from '../ui';

const MemberSelectionDialog = ({ open, onClose, members, onSelectMember }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredMembers = members.filter(member =>
    member.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.contact_no?.includes(searchTerm)
  );

  const handleSelectMember = (member) => {
    onSelectMember(member);
    setSearchTerm('');
    onClose();
  };

  const handleClose = () => {
    setSearchTerm('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'var(--panel-strong)',
          backdropFilter: 'blur(18px) saturate(130%)',
          WebkitBackdropFilter: 'blur(18px) saturate(130%)',
          border: '1px solid var(--stroke)',
          borderRadius: '18px',
          boxShadow: 'var(--shadow-card)',
          backgroundImage: 'none',
        },
      }}
      sx={{ '& .MuiBackdrop-root': { backgroundColor: 'rgba(5, 12, 20, 0.6)' } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, pb: 1.5 }}>
        <div>
          <Eyebrow>Families</Eyebrow>
          <h1 className="mt-2 font-display text-3xl font-bold leading-tight tracking-tight text-ink">
            Add <em className="kbc-serif">Family</em> Details
          </h1>
        </div>
        <IconButton
          onClick={handleClose}
          sx={{ color: 'var(--c-muted)', '&:hover': { color: 'var(--c-danger)' } }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 2, borderBottom: '1px solid var(--stroke)' }}>
          <TextField
            fullWidth
            placeholder="Search members..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <List sx={{ maxHeight: 400, overflow: 'auto' }}>
          {filteredMembers.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <EmptyState
                title="No members found"
                description={members.length === 0 ? 'All members already have family details' : 'Try adjusting your search'}
              />
            </Box>
          ) : (
            filteredMembers.map((member) => (
              <ListItem key={member.mid} disablePadding>
                <ListItemButton
                  onClick={() => handleSelectMember(member)}
                  sx={{ '&:hover': { backgroundColor: 'rgba(41, 245, 166, 0.06)' } }}
                >
                  <ListItemAvatar>
                    <Avatar
                      src={member.profile_image ? `${baseurl}/${member.profile_image}` : undefined}
                      name={member.first_name}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    sx={{ ml: 0.5 }}
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {member.first_name}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {member.email}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {member.contact_no}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))
          )}
        </List>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button variant="ghost" onClick={handleClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MemberSelectionDialog;
